import { useState, useCallback } from 'react';
import type { Game, ScoreUpdatedPayload, GameStateSyncPayload } from '../types/index.js';
import { computeStandings } from '../services/scoring.js';

export interface UseScoreboardReturn {
  game: Game | null;
  currentRoundNumber: number;
  team1Score: number;
  team2Score: number;
  isComplete: boolean;
  handleScoreUpdated: (payload: ScoreUpdatedPayload) => void;
  handleStateSync: (payload: GameStateSyncPayload) => void;
  setGame: (game: Game) => void;
}

/**
 * Manages local game state for the scoreboard display.
 * Applies optimistic updates and reconciles with server state.
 */
export function useScoreboard(): UseScoreboardReturn {
  const [game, setGameState] = useState<Game | null>(null);

  const setGame = useCallback((incoming: Game) => {
    setGameState(incoming);
  }, []);

  const handleScoreUpdated = useCallback((payload: ScoreUpdatedPayload) => {
    // Server is authoritative — replace with the full game state from the payload
    setGameState(payload.game);
  }, []);

  const handleStateSync = useCallback((payload: GameStateSyncPayload) => {
    setGameState(payload.game);
  }, []);

  const { team1Score, team2Score } = game
    ? computeStandings(game.rounds)
    : { team1Score: 0, team2Score: 0 };

  return {
    game,
    currentRoundNumber: game?.currentRound ?? 1,
    team1Score,
    team2Score,
    isComplete: game?.status === 'complete',
    handleScoreUpdated,
    handleStateSync,
    setGame,
  };
}
